import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { http, taskApi, matterApi, type Task, type MatterSummary } from '../../api';

// Slim matter shape we need for the popover.
type MatterRef = Pick<MatterSummary, 'ROWID' | 'reference' | 'matter_name'>;

// Consolidated calendar viewer — month / week / day grids that combine diary
// events (court dates, deadlines, consultations) with task due dates into one
// surface. Mirrors how an advocate actually plans their week.
//
// Data model: every item is normalised into `CalItem` regardless of source
// (event or task), so the grid renderers only deal with one shape.

type DiaryEvent = {
  ROWID: number;
  matter_id: number | null;
  kind: 'court_date' | 'deadline' | 'consultation' | 'conference' | 'reminder' | 'personal';
  title: string;
  description?: string | null;
  all_day: string | boolean;
  starts_at: string;
  ends_at?: string | null;
  severity: 'info' | 'normal' | 'warning' | 'critical' | 'statutory';
  statutory_reference?: string | null;
  location?: string | null;
};

type CalItem = {
  id: string;
  source: 'event' | 'task';
  /** YYYY-MM-DD local */
  date: string;
  /** "HH:MM" if scheduled, null for all-day */
  time: string | null;
  title: string;
  matter_id?: number | string | null;
  /** Hex / CSS colour for the dot / bar. */
  color: string;
  /** Sub-line ("Court date", "Task · high priority", etc.) */
  subline: string;
  /** Where clicking the item goes (matter detail by default). */
  link?: string;
};

type View = 'list' | 'month' | 'week' | 'day';

const asBool = (v: unknown) => v === true || v === 'true';

// ---------------------------------------------------------------------------
// Colour rules
// ---------------------------------------------------------------------------
function eventColor(severity: DiaryEvent['severity']): string {
  switch (severity) {
    case 'statutory': return 'var(--color-danger)';
    case 'critical':  return 'var(--color-danger)';
    case 'warning':   return 'var(--color-accent)';
    case 'info':      return 'var(--color-info)';
    default:          return 'var(--color-info)';
  }
}
function taskColor(priority: Task['priority']): string {
  switch (priority) {
    case 'urgent': return 'var(--color-danger)';
    case 'high':   return 'var(--color-danger)';
    case 'normal': return 'var(--color-success)';
    case 'low':    return 'var(--color-text-muted)';
    default:       return 'var(--color-success)';
  }
}

// ---------------------------------------------------------------------------
// Date helpers — work in local (browser) time so the displayed grid lines up
// with the advocate's wall clock. We only ever pass YYYY-MM-DD strings to the
// renderer; conversion to/from Date happens at boundaries.
// ---------------------------------------------------------------------------
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fromYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}
function startOfWeek(d: Date): Date {
  // Monday-first (SA / European convention — courts work Mon–Fri).
  const r = new Date(d);
  const dow = r.getDay(); // 0=Sun .. 6=Sat
  const offset = (dow + 6) % 7; // Monday = 0
  r.setDate(r.getDate() - offset);
  r.setHours(0, 0, 0, 0);
  return r;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function timeHHMM(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// ---------------------------------------------------------------------------
// Normalisers — diary event / task → CalItem
// ---------------------------------------------------------------------------
function eventToItem(e: DiaryEvent): CalItem {
  const date = e.starts_at.slice(0, 10);
  const allDay = asBool(e.all_day);
  return {
    id: `e:${e.ROWID}`,
    source: 'event',
    date,
    time: allDay ? null : timeHHMM(e.starts_at),
    title: e.title,
    matter_id: e.matter_id,
    color: eventColor(e.severity),
    subline:
      (e.kind === 'court_date' ? 'Court' :
       e.kind === 'deadline'   ? 'Deadline' :
       e.kind === 'consultation' ? 'Consultation' :
       e.kind === 'conference' ? 'Conference' :
       e.kind === 'reminder'   ? 'Reminder' : 'Personal') +
      (e.statutory_reference ? ` · ${e.statutory_reference}` : ''),
    link: e.matter_id ? `/matters/${e.matter_id}` : '/diary',
  };
}
function taskToItem(t: Task): CalItem | null {
  if (!t.due_date) return null;
  return {
    id: `t:${t.ROWID}`,
    source: 'task',
    date: t.due_date,
    time: t.due_time || null,
    title: t.name,
    matter_id: t.matter_id,
    color: taskColor(t.priority),
    subline: `Task${t.priority && t.priority !== 'normal' ? ` · ${t.priority}` : ''}`,
    link: t.matter_id ? `/matters/${t.matter_id}/tasks` : '/tasks',
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
type Props = {
  /** Initial view. Default is 'month'. */
  initialView?: View;
};

export default function CalendarViewer({ initialView = 'month' }: Props) {
  const [view, setView] = useState<View>(initialView);
  // `anchor` is the date the user is browsing — month, week, or day depends on view.
  const [anchor, setAnchor] = useState<Date>(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [events, setEvents] = useState<DiaryEvent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [matters, setMatters] = useState<Map<string, MatterRef>>(new Map());
  const [loading, setLoading] = useState(false);

  // Fetch matter references once for the hover-popover lookup. Pulled with
  // status undefined so finalised matters still resolve on the popover too.
  useEffect(() => {
    matterApi.list({}).then((r) => {
      const m = new Map<string, MatterRef>();
      for (const x of r.matters) m.set(String(x.ROWID), x);
      setMatters(m);
    }).catch(() => null);
  }, []);

  // Compute the date window to fetch for the current view.
  const window = useMemo(() => computeWindow(view, anchor), [view, anchor]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      http.get<{ events: DiaryEvent[] }>(`/diary?from=${window.fromYmd}&to=${window.toYmd}`).catch(() => ({ events: [] })),
      // Tasks aren't date-range-queryable on the API right now; pull open + completed once and filter client-side.
      taskApi.list({}).catch(() => ({ tasks: [] })),
    ]).then(([d, t]) => {
      if (cancelled) return;
      setEvents(d.events || []);
      setTasks(t.tasks || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [window.fromYmd, window.toYmd]);

  // Build the combined index: { 'YYYY-MM-DD': CalItem[] }
  const byDay = useMemo(() => {
    const out: Record<string, CalItem[]> = {};
    for (const e of events) {
      const it = eventToItem(e);
      (out[it.date] = out[it.date] || []).push(it);
    }
    for (const t of tasks) {
      const it = taskToItem(t);
      if (!it) continue;
      // Filter tasks to the visible window.
      if (it.date < window.fromYmd || it.date > window.toYmd) continue;
      (out[it.date] = out[it.date] || []).push(it);
    }
    // Sort each day by time (all-day first, then ascending).
    for (const day of Object.keys(out)) {
      out[day].sort((a, b) => {
        if (a.time === b.time) return a.title.localeCompare(b.title);
        if (a.time === null) return -1;
        if (b.time === null) return 1;
        return a.time.localeCompare(b.time);
      });
    }
    return out;
  }, [events, tasks, window.fromYmd, window.toYmd]);

  return (
    <div className="stack">
      {/* Toolbar — view tabs + navigation + label */}
      <div
        className="row"
        style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--color-border-strong)' }}>
          {(['month', 'week', 'day', 'list'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={view === v ? 'primary' : ''}
              style={{
                padding: '6px 14px',
                borderRadius: 0,
                border: 'none',
                fontSize: 13,
                background: view === v ? 'var(--color-accent)' : 'transparent',
                color: view === v ? '#1A1305' : 'var(--color-text)',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {v}
            </button>
          ))}
        </div>

        <button type="button" onClick={() => setAnchor(navigate(view, anchor, -1))} title="Previous">←</button>
        <button type="button" onClick={() => {
          const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d);
        }}>Today</button>
        <button type="button" onClick={() => setAnchor(navigate(view, anchor, 1))} title="Next">→</button>

        <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, padding: '0 8px' }}>
          {labelFor(view, anchor)}
        </div>

        {loading && <span className="muted" style={{ fontSize: 12 }}>Loading…</span>}
      </div>

      {view === 'month' && <MonthGrid anchor={anchor} byDay={byDay} matters={matters} />}
      {view === 'week'  && <WeekGrid  anchor={anchor} byDay={byDay} matters={matters} />}
      {view === 'day'   && <DayGrid   anchor={anchor} byDay={byDay} matters={matters} />}
      {view === 'list'  && <ListView  byDay={byDay} matters={matters} window={window} />}

      <Legend />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Range helpers
// ---------------------------------------------------------------------------
function computeWindow(view: View, anchor: Date): { fromYmd: string; toYmd: string } {
  if (view === 'day') {
    const d = ymd(anchor);
    return { fromYmd: d, toYmd: d };
  }
  if (view === 'week') {
    const s = startOfWeek(anchor);
    return { fromYmd: ymd(s), toYmd: ymd(addDays(s, 6)) };
  }
  if (view === 'month') {
    // Render a 6-row month grid that may bleed into prior / next month —
    // fetch a 42-day window starting from the first cell.
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    return { fromYmd: ymd(gridStart), toYmd: ymd(addDays(gridStart, 41)) };
  }
  // list — show 30 days from anchor
  return { fromYmd: ymd(anchor), toYmd: ymd(addDays(anchor, 30)) };
}

function navigate(view: View, anchor: Date, dir: -1 | 1): Date {
  const r = new Date(anchor);
  if (view === 'day')       r.setDate(r.getDate() + dir);
  else if (view === 'week') r.setDate(r.getDate() + 7 * dir);
  else                       r.setMonth(r.getMonth() + dir);
  return r;
}

function labelFor(view: View, anchor: Date): string {
  if (view === 'day') {
    return anchor.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (view === 'week') {
    const s = startOfWeek(anchor);
    const e = addDays(s, 6);
    return `${s.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (view === 'month') {
    return anchor.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  }
  return 'Upcoming 30 days';
}

// ---------------------------------------------------------------------------
// Hover popover — shows matter context + item detail when you hover any
// calendar entry. Click still navigates to the matter/task via the wrapped
// <Link>.
//
// Implementation notes:
//   - position: fixed (not absolute) so we escape any ancestor with
//     overflow: hidden (the month cell explicitly clips its children).
//   - Position is computed from the trigger's getBoundingClientRect() on
//     mouse enter, then re-aligned if the popover would fall off the
//     viewport bottom (renders above instead).
//   - pointer-events: none on the bubble so it doesn't steal the cursor —
//     the underlying Link stays hoverable and clickable through it.
//   - display: contents on the wrapper so it has no layout impact.
// ---------------------------------------------------------------------------

const POPOVER_WIDTH = 300;
const POPOVER_EST_HEIGHT = 160;

function HoverPopover({
  item, matter, children,
}: {
  item: CalItem;
  matter?: MatterRef | null;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);

  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const above = r.bottom + POPOVER_EST_HEIGHT + 12 > window.innerHeight;
    const top = above ? r.top - 6 : r.bottom + 6;
    // Clamp left so the bubble never falls off either edge.
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8));
    setPos({ top, left, above });
  };
  const onLeave = () => setPos(null);

  return (
    <span
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      // display: contents lets the wrapper carry events but take no space.
      style={{ display: 'contents' }}
    >
      {children}
      {pos && (
        <div
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: pos.above ? 'translateY(-100%)' : 'none',
            width: POPOVER_WIDTH,
            zIndex: 1000,
            pointerEvents: 'none',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
            padding: 12,
            fontSize: 12,
            color: 'var(--color-text)',
          }}
        >
          {/* Matter line — small, muted */}
          {matter ? (
            <div
              style={{
                fontSize: 11,
                color: 'var(--color-text-muted)',
                marginBottom: 8,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span style={{ fontFamily: 'ui-monospace, monospace' }}>{matter.reference}</span>
              {' · '}
              <span style={{ color: 'var(--color-text)' }}>{matter.matter_name || ''}</span>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {item.matter_id ? `Matter #${item.matter_id}` : 'No matter linked'}
            </div>
          )}

          {/* Title with colour bar */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{
              width: 4, minHeight: 28, alignSelf: 'stretch',
              background: item.color, borderRadius: 2, flexShrink: 0,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}>{item.title}</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                <span style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: 3,
                  background: item.source === 'task'
                    ? 'rgba(46, 189, 133, 0.14)'
                    : 'var(--color-surface-2)',
                  color: 'var(--color-text)',
                  fontWeight: 500,
                  marginRight: 6,
                  textTransform: 'capitalize',
                }}>{item.source}</span>
                {item.subline}
              </div>
            </div>
          </div>

          {/* When / where */}
          <div
            className="muted"
            style={{
              fontSize: 11,
              marginTop: 10,
              paddingTop: 8,
              borderTop: '1px solid var(--color-border)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {item.date}
            {item.time ? ` · ${item.time}` : ' · All day'}
          </div>
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Month grid — 7×6 cells, Monday-first.
// ---------------------------------------------------------------------------
function MonthGrid({ anchor, byDay, matters }: {
  anchor: Date;
  byDay: Record<string, CalItem[]>;
  matters: Map<string, MatterRef>;
}) {
  const monthStart = startOfMonth(anchor);
  const gridStart = startOfWeek(monthStart);
  const today = ymd(new Date());
  const monthIdx = anchor.getMonth();

  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = addDays(gridStart, i);
    const key = ymd(d);
    return {
      key, d,
      inMonth: d.getMonth() === monthIdx,
      isToday: key === today,
      items: byDay[key] || [],
    };
  });

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--color-border)' }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} style={dayHeader}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((c, i) => {
          const isWeekend = i % 7 >= 5;
          return (
            <div
              key={c.key}
              style={{
                ...monthCell,
                background: c.isToday
                  ? 'rgba(201,163,74,0.10)'
                  : isWeekend
                    ? 'var(--color-surface-2)'
                    : undefined,
                opacity: c.inMonth ? 1 : 0.45,
                borderTop: i >= 7 ? '1px solid var(--color-border)' : undefined,
                borderLeft: i % 7 !== 0 ? '1px solid var(--color-border)' : undefined,
              }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                marginBottom: 4,
              }}>
                <span style={{
                  fontSize: 12, fontWeight: c.isToday ? 700 : 500,
                  color: c.isToday ? 'var(--color-accent)' : 'var(--color-text)',
                }}>
                  {c.d.getDate()}
                </span>
                {c.items.length > 3 && (
                  <span className="muted" style={{ fontSize: 10 }}>{c.items.length} items</span>
                )}
              </div>
              <div className="stack" style={{ gap: 3 }}>
                {c.items.slice(0, 3).map((it) => (
                  <MonthItem key={it.id} item={it} matter={lookupMatter(matters, it)} />
                ))}
                {c.items.length > 3 && (
                  <div className="muted" style={{ fontSize: 10, paddingLeft: 8 }}>
                    + {c.items.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthItem({ item, matter }: { item: CalItem; matter?: MatterRef | null }) {
  // display:block + max-width 100% + whiteSpace:nowrap + textOverflow:ellipsis
  // is the only reliable way to clip in a CSS grid cell.
  return (
    <HoverPopover item={item} matter={matter}>
      <Link
        to={item.link || '/diary'}
        style={{
          display: 'block',
          maxWidth: '100%',
          padding: '2px 6px',
          borderRadius: 3,
          borderLeft: `3px solid ${item.color}`,
          background: 'var(--color-surface-2)',
          textDecoration: 'none',
          color: 'var(--color-text)',
          fontSize: 11,
          lineHeight: 1.35,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {item.time && (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            color: 'var(--color-text-muted)',
            marginRight: 4,
          }}>{item.time}</span>
        )}
        {item.title}
      </Link>
    </HoverPopover>
  );
}

// Resolve the matter ref for an item; works for events and tasks alike.
function lookupMatter(matters: Map<string, MatterRef>, item: CalItem): MatterRef | null {
  if (!item.matter_id) return null;
  return matters.get(String(item.matter_id)) || null;
}

// ---------------------------------------------------------------------------
// Week grid — 7 columns of daily agendas.
// ---------------------------------------------------------------------------
function WeekGrid({ anchor, byDay, matters }: {
  anchor: Date;
  byDay: Record<string, CalItem[]>;
  matters: Map<string, MatterRef>;
}) {
  const s = startOfWeek(anchor);
  const today = ymd(new Date());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(s, i);
    return { d, key: ymd(d), isToday: ymd(d) === today };
  });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10 }}>
      {days.map(({ d, key, isToday }) => (
        <div key={key} className="card" style={{
          padding: 12,
          borderColor: isToday ? 'var(--color-accent)' : undefined,
          minHeight: 280,
        }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {d.toLocaleDateString('en-ZA', { weekday: 'short' })}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: isToday ? 'var(--color-accent)' : 'var(--color-text)', marginBottom: 8 }}>
            {d.getDate()}
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {(byDay[key] || []).length === 0 ? (
              <div className="muted" style={{ fontSize: 11 }}>—</div>
            ) : (
              (byDay[key] || []).map((it) => (
                <AgendaItem key={it.id} item={it} matter={lookupMatter(matters, it)} />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day grid — full agenda for one day, with timed items vertically arranged.
// ---------------------------------------------------------------------------
function DayGrid({ anchor, byDay, matters }: {
  anchor: Date;
  byDay: Record<string, CalItem[]>;
  matters: Map<string, MatterRef>;
}) {
  const key = ymd(anchor);
  const items = byDay[key] || [];
  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {anchor.toLocaleDateString('en-ZA', { weekday: 'long' })}
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>
          {anchor.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>
      <div className="stack" style={{ gap: 0 }}>
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }} className="muted">
            Nothing scheduled for this day.
          </div>
        ) : (
          items.map((it, i) => {
            const matter = lookupMatter(matters, it);
            return (
              <HoverPopover key={it.id} item={it} matter={matter}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 18px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                }}>
                  <div style={{
                    width: 70, flexShrink: 0,
                    fontSize: 13, fontVariantNumeric: 'tabular-nums',
                    color: it.time ? 'var(--color-text)' : 'var(--color-text-muted)',
                  }}>
                    {it.time || 'All day'}
                  </div>
                  <div style={{
                    width: 4, alignSelf: 'stretch', background: it.color, borderRadius: 2,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={it.link || '/diary'} style={{ color: 'var(--color-text)', textDecoration: 'none', fontWeight: 500 }}>
                      {it.title}
                    </Link>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {it.subline}
                      {matter ? <> · {matter.reference}</> : (it.matter_id ? <> · matter #{it.matter_id}</> : null)}
                    </div>
                  </div>
                </div>
              </HoverPopover>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List view — original Diary layout, kept for users who prefer it.
// ---------------------------------------------------------------------------
function ListView({
  byDay, matters, window,
}: {
  byDay: Record<string, CalItem[]>;
  matters: Map<string, MatterRef>;
  window: { fromYmd: string; toYmd: string };
}) {
  const days = Object.keys(byDay).sort();
  if (days.length === 0) {
    return (
      <div className="card">
        <p className="muted">
          No events or tasks between {window.fromYmd} and {window.toYmd}.
        </p>
      </div>
    );
  }
  return (
    <div className="stack">
      {days.map((day) => (
        <div key={day}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            {fromYmd(day).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {byDay[day].map((it, i) => {
              const matter = lookupMatter(matters, it);
              return (
                <HoverPopover key={it.id} item={it} matter={matter}>
                  <div style={{
                    padding: 14,
                    borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                    display: 'flex', gap: 12,
                  }}>
                    <div style={{ width: 4, alignSelf: 'stretch', background: it.color, borderRadius: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Link to={it.link || '/diary'} style={{ color: 'var(--color-text)', textDecoration: 'none', fontWeight: 500 }}>
                        {it.title}
                      </Link>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {it.time || 'All day'} · {it.subline}
                        {matter ? <> · {matter.reference}</> : (it.matter_id ? <> · matter #{it.matter_id}</> : null)}
                      </div>
                    </div>
                  </div>
                </HoverPopover>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
function AgendaItem({ item, matter }: { item: CalItem; matter?: MatterRef | null }) {
  return (
    <HoverPopover item={item} matter={matter}>
      <Link
        to={item.link || '/diary'}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 6,
          textDecoration: 'none', color: 'var(--color-text)',
          padding: '4px 6px',
          background: 'var(--color-surface-2)',
          borderRadius: 4,
          borderLeft: `3px solid ${item.color}`,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.title}
          </div>
          <div className="muted" style={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
            {item.time || 'All day'}
          </div>
        </span>
      </Link>
    </HoverPopover>
  );
}

function Legend() {
  return (
    <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: 11 }}>
      <span className="muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Legend</span>
      <LegendDot color="var(--color-danger)"  label="Court / statutory / urgent" />
      <LegendDot color="var(--color-accent)"  label="Warning deadline" />
      <LegendDot color="var(--color-info)"    label="Other event" />
      <LegendDot color="var(--color-success)" label="Task due" />
    </div>
  );
}
function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {label}
    </span>
  );
}

const dayHeader: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 11, fontWeight: 600,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  textAlign: 'left',
  borderLeft: '1px solid var(--color-border)',
};
const monthCell: React.CSSProperties = {
  minHeight: 110,
  padding: 6,
  display: 'flex',
  flexDirection: 'column',
  // overflow:hidden clips items that exceed the cell width.
  // min-width:0 is the magic incantation that makes the clip ACTUALLY work
  // on a CSS grid child — without it, the cell expands to fit its content
  // and trampolines into the next column.
  overflow: 'hidden',
  minWidth: 0,
};
